import React, { useEffect, useState } from 'react';
import {
  Navigate, useNavigate, Outlet,
} from 'react-router-dom';
import { Location } from 'history';
import { FpLayoutEditLeaveConfirm } from 'components/molecules/dialog/FpLayoutEditConfirm';
import { useLayoutUpdateFacade } from 'redux/layout-update/layout-update.facade';
import { useLayoutEditState } from 'components/pages/layout-edit/edit-logic';

/**
 * 管理者用レイアウト編集画面用ガード
 * @description in: レイアウトを選択してなければ一覧へ飛ばす, out: 保存してなければ確認する
 */
export function FpAdminLayoutEditGuard() {
  const navigate = useNavigate();
  const [modalVisible, setModalVisible] = useState(false);
  const [lastLocation, setLastLocation] = useState<Location | null>(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  const { selectedLayout } = useLayoutUpdateFacade();
  const { editing, exitLayoutEdit } = useLayoutEditState();

  const enablePath = [
    /\/admin\/layout\/preview/,
    /\/admin\/layout\/edit\/main_menu/,
    /\/admin\/layout\/edit\/main_menu\/file_search/,
    /\/admin\/layout\/edit\/main_menu\/folder-search/,
    /\/admin\/layout\/edit/,
    /\/admin\/search/,
    /\/search/,
    /\/error/,
  ];
  const navigateable = (to: string): boolean => enablePath
    .map((path) => path.test(to))
    .filter(Boolean)
    .length > 0;

  // Warn on browser/tab close
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (editing) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [editing]);

  // Listen for navigation events (popstate for browser back/forward)
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (!confirmedNavigation && editing) {
        const nextPath = window.location.pathname;
        if (!navigateable(nextPath)) {
          setLastLocation({ pathname: nextPath } as Location);
          setModalVisible(true);
          // Revert navigation
          navigate(-1);
        }
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [confirmedNavigation, editing, navigate, navigateable]);

  // After confirmation, proceed with navigation
  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      navigate(lastLocation.pathname);
      setConfirmedNavigation(false);
      setLastLocation(null);
    }
  }, [confirmedNavigation, lastLocation, navigate]);

  const handleConfirmNavigationClick = () => {
    setModalVisible(false);
    setConfirmedNavigation(true);
    exitLayoutEdit();
  };

  const closeDialog = () => {
    setModalVisible(false);
    setLastLocation(null);
  };

  return (
    <>
      {modalVisible && (
        <FpLayoutEditLeaveConfirm
          onConfirm={handleConfirmNavigationClick}
          onClose={closeDialog}
        />
      )}
      {selectedLayout
        ? <Outlet />
        : <Navigate to="/admin/layout_role_assign" replace />}
    </>
  );
}
